import { useState, useEffect } from 'react'
import { useClientPreset } from '@/contexts/ClientPresetContext'

interface AnalyticsData {
  totalCampaigns: number
  activeCampaigns: number
  totalSpend: number
  totalImpressions: number
  totalClicks: number
  averageCTR: number
  averageCPC: number
  totalConversions: number
  conversionRate: number
  topPerformingCampaigns: Array<{
    id: string
    name: string
    spend: number
    impressions: number
    clicks: number
    ctr: number
    conversions: number
  }>
  campaignPerformanceOverTime: Array<{
    date: string
    spend: number
    impressions: number
    clicks: number
    conversions: number
  }>
}

export default function AnalyticsPage() {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [error, setError] = useState<string | null>(null)
  const { selectedPreset } = useClientPreset()

  const fetchAnalytics = async () => {
    if (!selectedPreset) return
    
    setLoading(true)
    setError(null)
    
    try {
      // For now, we'll use mock data since the analytics API might not be fully implemented
      // In production, this would be: const response = await api.get(`/analytics/${selectedPreset.id}?timeRange=${timeRange}`)
      
      // Mock data for demonstration
      const mockData: AnalyticsData = {
        totalCampaigns: 12,
        activeCampaigns: 8,
        totalSpend: 45230.50,
        totalImpressions: 2850000,
        totalClicks: 68500,
        averageCTR: 2.4,
        averageCPC: 0.66,
        totalConversions: 3420,
        conversionRate: 4.99,
        topPerformingCampaigns: [
          {
            id: 'camp_1',
            name: 'Holiday Fashion Collection',
            spend: 8500,
            impressions: 520000,
            clicks: 15200,
            ctr: 2.92,
            conversions: 760
          },
          {
            id: 'camp_2', 
            name: 'Summer Electronics Sale',
            spend: 6200,
            impressions: 380000,
            clicks: 9100,
            ctr: 2.39,
            conversions: 455
          },
          {
            id: 'camp_3',
            name: 'Back to School Essentials',
            spend: 4800,
            impressions: 290000,
            clicks: 8200,
            ctr: 2.83,
            conversions: 390
          }
        ],
        campaignPerformanceOverTime: Array.from({ length: 30 }, (_, i) => ({
          date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          spend: Math.random() * 2000 + 500,
          impressions: Math.random() * 100000 + 50000,
          clicks: Math.random() * 3000 + 1000,
          conversions: Math.random() * 150 + 50
        }))
      }
      
      setAnalyticsData(mockData)
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [selectedPreset, timeRange])

  if (!selectedPreset) {
    return (
      <div className="page-container">
        <div className="card-empty-state">
          <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-2)' }}>📊</div>
          <h2>Analytics Dashboard</h2>
          <p>Select a client to view campaign analytics and performance metrics.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="card-loading">
          <div className="loading-spinner"></div>
          <span>Loading analytics data...</span>
        </div>
      </div>
    )
  }

  if (error || !analyticsData) {
    return (
      <div className="page-container">
        <div className="card-error">
          <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-2)' }}>⚠️</div>
          <h2>Analytics Unavailable</h2>
          <p>{error || 'Unable to load analytics data at this time.'}</p>
          <button onClick={fetchAnalytics} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics Dashboard</h1>
          <p className="page-subtitle">
            Campaign performance insights for {selectedPreset.brand}
          </p>
        </div>
        
        <div className="header-actions">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
            className="select-field"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <div className="metric-value">{analyticsData.totalCampaigns}</div>
            <div className="metric-label">Total Campaigns</div>
            <div className="metric-sublabel">{analyticsData.activeCampaigns} active</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <div className="metric-value">${analyticsData.totalSpend.toLocaleString()}</div>
            <div className="metric-label">Total Spend</div>
            <div className="metric-sublabel">${analyticsData.averageCPC.toFixed(2)} avg CPC</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">👁️</div>
          <div className="metric-content">
            <div className="metric-value">{(analyticsData.totalImpressions / 1000000).toFixed(1)}M</div>
            <div className="metric-label">Impressions</div>
            <div className="metric-sublabel">{analyticsData.totalClicks.toLocaleString()} clicks</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">🎯</div>
          <div className="metric-content">
            <div className="metric-value">{analyticsData.averageCTR.toFixed(2)}%</div>
            <div className="metric-label">Average CTR</div>
            <div className="metric-sublabel">{analyticsData.conversionRate.toFixed(2)}% conv. rate</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="analytics-content">
        <div className="analytics-section">
          <h2 className="section-title">Performance Over Time</h2>
          <div className="chart-placeholder">
            <div style={{
              width: '100%',
              height: '300px',
              background: 'var(--color-systemGroupedBackground)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 'var(--spacing-2)',
              color: 'var(--color-secondaryLabel)'
            }}>
              <div style={{ fontSize: '32px' }}>📈</div>
              <div>Performance chart would be displayed here</div>
              <div style={{ fontSize: 'var(--font-size-sm)', opacity: 0.7 }}>
                Integration with charting library needed (Chart.js, Recharts, etc.)
              </div>
            </div>
          </div>
        </div>

        <div className="analytics-section">
          <h2 className="section-title">Top Performing Campaigns</h2>
          <div className="campaigns-table">
            {analyticsData.topPerformingCampaigns.map((campaign, index) => (
              <div key={campaign.id} className="campaign-row">
                <div className="campaign-rank">#{index + 1}</div>
                <div className="campaign-info">
                  <div className="campaign-name">{campaign.name}</div>
                  <div className="campaign-metrics">
                    <span className="metric-chip">
                      💰 ${campaign.spend.toLocaleString()}
                    </span>
                    <span className="metric-chip">
                      👁️ {(campaign.impressions / 1000).toFixed(0)}K
                    </span>
                    <span className="metric-chip">
                      🖱️ {campaign.clicks.toLocaleString()}
                    </span>
                    <span className="metric-chip">
                      📊 {campaign.ctr.toFixed(2)}% CTR
                    </span>
                    <span className="metric-chip">
                      🎯 {campaign.conversions} conv.
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="action-cards">
        <div className="action-card">
          <div className="action-icon">🚀</div>
          <div className="action-content">
            <h3>Optimize Campaigns</h3>
            <p>Review underperforming campaigns and optimize targeting, creatives, or budgets.</p>
            <button className="btn btn-secondary btn-sm">
              View Recommendations
            </button>
          </div>
        </div>

        <div className="action-card">
          <div className="action-icon">📋</div>
          <div className="action-content">
            <h3>Export Report</h3>
            <p>Download detailed analytics report for stakeholder presentations.</p>
            <button className="btn btn-secondary btn-sm">
              Generate Report
            </button>
          </div>
        </div>

        <div className="action-card">
          <div className="action-icon">⚡</div>
          <div className="action-content">
            <h3>Create Campaign</h3>
            <p>Launch new campaigns based on your best-performing creative assets.</p>
            <button className="btn btn-primary btn-sm">
              Start Creating
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
